import { Button, Message, Result, Space, Spin, Typography } from "@arco-design/web-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Calculator, Eye, ReceiptText, UsersRound } from "lucide-react";

import { canUsePermission } from "../../../lib/auth";
import { isHttpClientError } from "../../../lib/http/errors";
import { useUnsavedChangesGuard } from "../../../shared/navigation/useUnsavedChangesGuard";
import { useBusinessPageTitle } from "../../../lib/navigation/useBusinessPageTitle";
import { useCurrentAuthUser } from "../../auth";
import { FeeAdjustmentWorkspacePanel } from "../../fee-adjustment";
import { confirmationCategoryForLine } from "../service/confirmationEditorView";
import { buildTicketVisits } from "../service/confirmationTicketWorkspace";
import { formatConfirmationAmount } from "../service/confirmationDecimalDisplay";
import { validateFulfillmentLinesBeforeFinalize, validateFulfillmentLinesBeforeSave } from "../service/fulfillmentOrderEditorService";
import type { ConfirmationFeedback } from "./editor/ConfirmationActionFeedback";
import { FulfillmentOrderFeeHistoryPanel } from "./FulfillmentOrderFeeHistoryPanel";
import { buildItineraryBudgetPath, buildItineraryPreviewPath } from "../../itinerary";
import { FULFILLMENT_ORDER_PERMISSIONS } from "../config";
import { useFulfillmentOrderEditorRuntime, useFulfillmentOrderLifecycleRuntime } from "../runtime";
import {
  FulfillmentOrderUserFacingError,
  getSafeFulfillmentOrderActionErrorMessage,
} from "../service";
import { toLegacyFulfillmentEditor } from "../repo/courseRouteFulfillmentRepo";
import type {
  FulfillmentOrderLine,
  FulfillmentWorkspaceEditor,
} from "../types";
import { FulfillmentOrderFeeAdjustmentQuickEntry } from "./FulfillmentOrderBatchAdjustmentQuickEntry";
import { FulfillmentOrderLineDetailDrawer } from "./FulfillmentOrderLineDetailDrawer";
import { FulfillmentOrderHistoryDrawer } from "./FulfillmentOrderHistoryDrawer";
import { ConfirmationEditorLinePool } from "./editor/ConfirmationEditorLinePool";
import { ConfirmationSidebar } from "./editor/ConfirmationSidebar";
import { ConfirmationStayEntryDrawer } from "./editor/ConfirmationStayEntryDrawer";
import { ConfirmationAirEntryDrawer } from "./editor/ConfirmationAirEntryDrawer";
import { ConfirmationShipEntryDrawer } from "./editor/ConfirmationShipEntryDrawer";
import { ConfirmationBusEntryDrawer } from "./editor/ConfirmationBusEntryDrawer";
import { ConfirmationTicketEntryDrawer } from "./editor/ConfirmationTicketEntryDrawer";
import { ConfirmationMealEntryDrawer } from "./editor/ConfirmationMealEntryDrawer";
import { ConfirmationCrewEntryDrawer } from "./editor/ConfirmationCrewEntryDrawer";
import { ConfirmationMiscEntryDrawer } from "./editor/ConfirmationMiscEntryDrawer";
import { ConfirmationRailwayDrawer } from "./editor/ConfirmationRailwayDrawer";
import { ConfirmationSubjects } from "./editor/ConfirmationSubjects";
import { ConfirmationButton, ConfirmationSheet } from "./editor/primitives";
import type { ConfirmationCategory, ConfirmationBatchChange } from "../types";
import "./editor/workspace.css";
import "./editor/adjustmentWorkspace.css";
import { FulfillmentOrderQuickAdjustmentModal } from "./FulfillmentOrderQuickAdjustmentModal";
import { ConfirmationSettlementGroups } from "./editor/ConfirmationSettlementGroups";
import { buildConfirmationSettlementGroups } from "../service/confirmationSettlementService";
import "./fulfillmentOrder.css";

/** 兼容旧调用方缺少独立状态字段；明确的 null/false 不得被聚合状态覆盖。 */
function specificStateOrLegacy<T>(specific: T | undefined, legacy: T): T {
  return specific === undefined ? legacy : specific;
}

type GenerateValidationFeedback = {
  message: string;
  lineNo: number | null;
};

/** 反馈只描述这份明细和业务版本，不把后续编辑或刷新冒充本次操作结果。 */
function actionFeedbackSnapshot(editor: FulfillmentWorkspaceEditor | null, lines: FulfillmentOrderLine[]): string {
  return JSON.stringify([editor?.workspace.id, editor?.workspace.revision, editor?.workspace.status, lines]);
}

type ScopedActionFeedback = ConfirmationFeedback & { snapshot: string };

const GENERATE_ERROR_FALLBACK = "生成协议失败，请稍后重试";

/** 将普通生成异常转换成稳定的用户提示。 */
function generateErrorMessage(error: unknown): string {
  return getSafeFulfillmentOrderActionErrorMessage(error, GENERATE_ERROR_FALLBACK);
}

/** 将生成校验异常收敛为可直接修正的业务提示，不展示内部错误码和堆栈。 */
function generateValidationFeedback(error: unknown): GenerateValidationFeedback | null {
  const rawMessage = error instanceof FulfillmentOrderUserFacingError ? error.message : "";
  if (!rawMessage.includes("生成前校验未通过")) return null;
  const message = rawMessage;
  const lineMatch = message.match(/(?:第\s*(\d+)\s*行|#\s*(\d+))/u);
  const lineNoText = lineMatch?.[1] ?? lineMatch?.[2];
  return {
    message: message || "生成前校验未通过，请检查确认行后重试。",
    lineNo: lineNoText ? Number(lineNoText) : null,
  };
}

/** 确认件工作区编辑器：来源摘要、确认行池、批量归口和供应商协议。 */
export function FulfillmentOrderEditorFeaturePage({ workspaceId }: { workspaceId: string }) {
  const userQuery = useCurrentAuthUser();
  const user = userQuery.data;
  const canUpdate = canUsePermission(user, FULFILLMENT_ORDER_PERMISSIONS.update);
  const canFinalize = canUsePermission(user, FULFILLMENT_ORDER_PERMISSIONS.finalize);
  const canExport = canUsePermission(user, FULFILLMENT_ORDER_PERMISSIONS.export);
  const canArchive = canUsePermission(user, FULFILLMENT_ORDER_PERMISSIONS.archive);
  const canAccessFeeAdjustment = canUsePermission(
    user,
    "fee_adjustment.processing:access",
  );
  const canUpdateFeeAdjustment = canUsePermission(
    user,
    "fee_adjustment.processing:update",
  );
  const canFinalizeFeeAdjustment = canUsePermission(
    user,
    "fee_adjustment.processing:finalize",
  );
  const [changeHistoryLine, setChangeHistoryLine] = useState<FulfillmentOrderLine | null>(null);
  const runtime = useFulfillmentOrderEditorRuntime(workspaceId, canUpdate, canFinalize);
  const beforeFinalization = ["draft", "ready"].includes(runtime.editor?.workspace.status ?? "");
  useBusinessPageTitle(runtime.editor?.workspace.id === workspaceId && runtime.editor.workspace.itineraryTitle
    ? `确认件 · ${runtime.editor.workspace.itineraryTitle}` : undefined);
  const lifecycle = useFulfillmentOrderLifecycleRuntime(
    runtime.orderDetail?.order.id ?? null,
    runtime.orderDetail?.order.documentRevision ?? null,
  );
  const [editingLine, setEditingLine] = useState<FulfillmentOrderLine | null>(null);
  const [adjustmentDirty, setAdjustmentDirty] = useState(false);
  const [feeAdjustmentDirty, setFeeAdjustmentDirty] = useState(false);
  const [feeAdjustmentPending, setFeeAdjustmentPending] = useState(false);
  const [entryCategory, setEntryCategory] = useState<ConfirmationCategory | null>(null);
  const [railwayEntryOpen, setRailwayEntryOpen] = useState(false);
  const [entryNewOnly, setEntryNewOnly] = useState(false);
  const [entryExistingLineId, setEntryExistingLineId] = useState<string | null>(null);
  const [ticketInitialVisitId, setTicketInitialVisitId] = useState<string | null>(null);
  const [ticketDirtyVisitIds, setTicketDirtyVisitIds] = useState<string[]>([]);
  const ticketVisits = useMemo(() => runtime.editor ? buildTicketVisits(runtime.editor.workspace) : [], [runtime.editor?.workspace]);
  const [entryGeneration, setEntryGeneration] = useState(0);
  const [formulaDirty, setFormulaDirty] = useState(false);
  const [entryDirty, setEntryDirty] = useState<Partial<Record<ConfirmationCategory, boolean>>>({});
  const pendingEntryText = Object.values(entryDirty).some(Boolean);
  const [newLineNo, setNewLineNo] = useState<number | null>(null);
  const [revealLineId, setRevealLineId] = useState<string | null>(null);
  const hasOrdinaryUnappliedWork = adjustmentDirty || pendingEntryText || formulaDirty;
  const hasUnappliedWork = hasOrdinaryUnappliedWork || feeAdjustmentDirty || feeAdjustmentPending;
  const hasUnsavedWork = runtime.isDirty || hasUnappliedWork;
  const unsavedChangesGuard = useUnsavedChangesGuard(hasUnsavedWork);
  const [quickAdjustmentLine, setQuickAdjustmentLine] = useState<FulfillmentOrderLine | null>(null);
  const [feeAdjustmentLine, setFeeAdjustmentLine] = useState<FulfillmentOrderLine | null>(null);
  const [feeAdjustmentDrawerVisible, setFeeAdjustmentDrawerVisible] = useState(false);
  const [feeAdjustmentRequestId, setFeeAdjustmentRequestId] = useState<string | null>(null);
  const feeAdjustmentEditing = feeAdjustmentRequestId !== null;
  const [feeAdjustmentRequestText, setFeeAdjustmentRequestText] = useState<string | null>(null);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<ScopedActionFeedback | null>(null);
  const actionRequestGenerationRef = useRef(0);
  const currentActionSnapshot = actionFeedbackSnapshot(runtime.editor, runtime.lines);
  const activeActionSnapshotRef = useRef(currentActionSnapshot);
  activeActionSnapshotRef.current = currentActionSnapshot;
  const unappliedWorkRef = useRef(hasUnappliedWork);
  unappliedWorkRef.current = hasUnappliedWork;
  const [generateValidation, setGenerateValidation] = useState<GenerateValidationFeedback | null>(null);
  const generateRequestGenerationRef = useRef(0);
  const activeWorkspaceIdRef = useRef(workspaceId);
  activeWorkspaceIdRef.current = workspaceId;
  const rawQuickEntryCandidates = runtime.quickEntryFulfillmentSupplierOptions ?? runtime.fulfillmentSupplierOptions;
  const quickEntrySupplierLoading = specificStateOrLegacy(
    runtime.quickEntrySupplierOptionsLoading,
    runtime.supplierOptionsLoading,
  );
  const quickEntrySupplierErrorCode = specificStateOrLegacy(
    runtime.quickEntrySupplierOptionsErrorCode,
    runtime.supplierOptionsErrorCode,
  );
  const quickEntrySupplierError = specificStateOrLegacy(
    runtime.quickEntrySupplierOptionsError,
    runtime.supplierOptionsError,
  );
  const retryQuickEntrySuppliers = specificStateOrLegacy(
    runtime.refetchQuickEntrySupplierOptions,
    runtime.refetchSupplierOptions,
  );
  const settlementSupplierLoading = specificStateOrLegacy(
    runtime.settlementSupplierOptionsLoading,
    runtime.supplierOptionsLoading,
  );
  const settlementSupplierErrorCode = specificStateOrLegacy(
    runtime.settlementSupplierOptionsErrorCode,
    runtime.supplierOptionsErrorCode,
  );
  const settlementSupplierError = specificStateOrLegacy(
    runtime.settlementSupplierOptionsError,
    runtime.supplierOptionsError,
  );
  const retrySettlementSuppliers = specificStateOrLegacy(
    runtime.refetchSettlementSupplierOptions,
    runtime.refetchSupplierOptions,
  );
  const readonlyLineIds = useMemo(() => {
    if (!canUpdate || (feeAdjustmentEditing && !beforeFinalization)) {
      return new Set((runtime.lines ?? []).map((line) => line.id));
    }
    const lockedOrderIds = new Set(
      (runtime.orders ?? [])
        .filter((order) => order.status !== "draft" && order.status !== "effective")
        .map((order) => order.id),
    );
    return new Set(
      (runtime.lines ?? [])
        .filter((line) => Boolean(
          line.fulfillmentOrderId && lockedOrderIds.has(line.fulfillmentOrderId),
        ))
        .map((line) => line.id),
    );
  }, [canUpdate, feeAdjustmentEditing, beforeFinalization, runtime.lines, runtime.orders]);
  const adjustableLineIds = useMemo(() => {
    if (!canUpdate) return new Set<string>();
    if (!beforeFinalization) {
      return new Set(
        (runtime.lines ?? [])
          .filter((line) => !readonlyLineIds.has(line.id))
          .map((line) => line.id),
      );
    }
    const effectiveOrderIds = new Set(
      (runtime.orders ?? [])
        .filter((order) => order.status === "effective")
        .map((order) => order.id),
    );
    return new Set(
      (runtime.lines ?? [])
        .filter((line) => !readonlyLineIds.has(line.id)
          && line.fulfillmentOrderId
          && effectiveOrderIds.has(line.fulfillmentOrderId))
        .map((line) => line.id),
    );
  }, [canUpdate, readonlyLineIds, beforeFinalization, runtime.lines, runtime.orders]);
  useEffect(() => {
    setEditingLine(null);
    setAdjustmentDirty(false);
    setFeeAdjustmentDirty(false);
    setFeeAdjustmentPending(false);
    setFeeAdjustmentRequestId(null);
    setFeeAdjustmentRequestText(null);
    setFeeAdjustmentLine(null);
    setFeeAdjustmentDrawerVisible(false);
    setQuickAdjustmentLine(null);
    setNewLineNo(null);
    setRevealLineId(null);
    setActionFeedback(null);
    setChangeHistoryLine(null);
    setEntryCategory(null);
    setEntryExistingLineId(null);
    setTicketInitialVisitId(null);
    setTicketDirtyVisitIds([]);
    setRailwayEntryOpen(false);
    setEntryDirty({});
  }, [workspaceId]);
  useEffect(() => {
    generateRequestGenerationRef.current += 1;
    setGenerateValidation(null);
  }, [workspaceId]);
  useEffect(() => {
    actionRequestGenerationRef.current += 1;
    return () => { actionRequestGenerationRef.current += 1; };
  }, [workspaceId]);
  useEffect(() => {
    // 只在内容变化时失效；设置新成功反馈自身不会触发清除，权威回写也能保留它。
    setActionFeedback(current => current && current.snapshot !== currentActionSnapshot ? null : current);
  }, [currentActionSnapshot]);
  useEffect(() => {
    if (!hasUnappliedWork) return;
    // 尚未加入行池的输入同样使旧反馈失效；保存自身的 dirty→clean 不触发此分支。
    actionRequestGenerationRef.current += 1;
    setActionFeedback(null);
  }, [hasUnappliedWork]);

  if (runtime.isLoading && (!runtime.editor || runtime.editor.workspace.id !== workspaceId)) {
    return <div className="fulfillment-order-loading"><Spin dot /></div>;
  }
  if (!runtime.editor || runtime.editor.workspace.id !== workspaceId) {
    const workspaceNotFound = isHttpClientError(runtime.loadError)
      && runtime.loadError.statusCode === 404;
    return (
      <Result
        status="error"
        title={workspaceNotFound ? "工作区不存在或已删除" : "确认件工作区暂时无法加载"}
        subTitle={workspaceNotFound
          ? "该确认件工作区可能已被删除，请返回课程线路重新选择。"
          : "网络或服务暂时异常，请稍后重试。"}
        extra={(
          <Space>
            <Button href="/product-lines/course-routes">返回课程线路</Button>
            {workspaceNotFound ? null : (
              <Button type="primary" onClick={() => runtime.refetch()}>重试</Button>
            )}
          </Space>
        )}
      />
    );
  }

  const pendingHistoryLines = runtime.lines.filter((line) => (
    line.status === "active"
    && !line.fulfillmentOrderId
    && line.reviewStatus === "pending"
  ));
  const pendingHistoryDateCount = pendingHistoryLines.filter(
    (line) => line.dateMappingStatus !== "mapped",
  ).length;
  const selectedLines = runtime.lines.filter(
    (line) => runtime.selectedIds.has(line.id) && runtime.selectableLineIds.has(line.id),
  );
  const generateValidationLine = generateValidation?.lineNo === null
    ? null
    : runtime.lines.find((line) => line.lineNo === generateValidation?.lineNo) ?? null;

  const ordinaryEditableIds = new Set(runtime.lines.filter(line => line.status === "active" && !readonlyLineIds.has(line.id) && !adjustableLineIds.has(line.id)).map(line => line.id));
  const entryEditDisabledReason = beforeFinalization ? undefined : "已定稿费用请通过行内“增减”修改。";
  const openCategoryEntry = (category: ConfirmationCategory, newOnly = false) => {
    if (!newOnly) {
      if (entryEditDisabledReason) { Message.error(entryEditDisabledReason); return; }
      if (runtime.lines.some(line => runtime.selectedIds.has(line.id)
        && confirmationCategoryForLine(line) === category && !ordinaryEditableIds.has(line.id))) {
        Message.error("所选本类明细含已锁定或需通过增减修改的费用，请取消这些勾选后重试。");
        return;
      }
    }
    setTicketInitialVisitId(null);
    setEntryExistingLineId(null);
    setEntryNewOnly(newOnly);
    setEntryCategory(category);
    setRailwayEntryOpen(category === "train");
  };
  const openLineEditor = (line: FulfillmentOrderLine) => {
    const category = confirmationCategoryForLine(line);
    setTicketInitialVisitId(null);
    if (category && ordinaryEditableIds.has(line.id)) {
      setEditingLine(null);
      setEntryExistingLineId(line.id);
      setEntryNewOnly(false);
      setEntryCategory(category);
      setRailwayEntryOpen(category === "train");
      return;
    }
    setEditingLine(line);
  };
  const currentEditor = runtime.editor;
  const applyBatch = async (change: ConfirmationBatchChange) => {
    if (!canUpdate || feeAdjustmentEditing || runtime.writePending) throw new FulfillmentOrderUserFacingError("当前暂时不能修改确认件");
    if (!beforeFinalization) {
      if (change.updates.length || change.removals.length) throw new FulfillmentOrderUserFacingError("已定稿明细请通过原增减操作修改");
      const saved = await runtime.addFinalizedLines(change.additions);
      if (activeWorkspaceIdRef.current !== workspaceId) return;
      const previousIds = new Set(runtime.lines.map(line => line.id));
      const added = saved?.lines.filter(line => !previousIds.has(line.id) && line.status === "active") ?? [];
      if (added.length) setNewLineNo(added.at(-1)!.lineNo);
      Message.success("确认明细已新增并保存");
    } else {
      const added = runtime.applyDraftBatch(change);
      if (added?.length) setNewLineNo(added.at(-1)!.lineNo);
      Message.success("确认明细已更新，请检查后保存草稿");
    }
  };

  const settlementGroups = buildConfirmationSettlementGroups({
    workspaceStatus: currentEditor.workspace.status,
    lines: runtime.lines,
    orders: runtime.orders,
  });
  const generateForSubject = async (supplierId: string, pendingLineIds: readonly string[]) => {
    if (hasUnappliedWork || feeAdjustmentEditing || !canUpdate || runtime.writePending) return;
    const requestGeneration = ++generateRequestGenerationRef.current;
    const requestWorkspaceId = workspaceId;
    const isCurrentRequest = () => requestGeneration === generateRequestGenerationRef.current
      && requestWorkspaceId === activeWorkspaceIdRef.current;
    setGenerateValidation(null);
    try {
      await runtime.generateSelected(supplierId, pendingLineIds);
      if (isCurrentRequest()) Message.success("协议已生成");
    } catch (error) {
      if (!isCurrentRequest()) return;
      const validation = generateValidationFeedback(error);
      if (validation) setGenerateValidation(validation);
      else Message.error(generateErrorMessage(error));
    }
  };

  const renderGenerateValidation = (close?: () => void) => generateValidation ? (
    <div className="fulfillment-order-inline-warning" role="alert">
      <span>{generateValidation.message}</span>
      {generateValidationLine ? <ConfirmationButton variant="ghost" size="sm" onClick={() => { close?.(); openLineEditor(generateValidationLine); }}>编辑第 {generateValidationLine.lineNo} 行</ConfirmationButton> : null}
    </div>
  ) : null;

  return (
    <main className="fulfillment-order-editor-page confirmation-editor-theme" aria-label="确认件编辑器">
      {unsavedChangesGuard.blocker}
      <header className="confirmation-workspace-header">
        <div className="confirmation-heading"><div className="confirmation-heading-title"><h1>确认件</h1><span className="confirmation-heading-status">{beforeFinalization ? "草稿" : "已定稿"}</span><span className="confirmation-heading-status" data-unsaved={runtime.isDirty || undefined}>{runtime.isDirty ? "未保存" : "已保存"}</span></div><nav className="fulfillment-order-related-links" aria-label="确认件关联业务快速入口">
        <span className="fulfillment-order-related-links__label">关联业务</span>
        <div className="fulfillment-order-related-links__items">
          <a className="fulfillment-order-related-links__link" href={buildItineraryPreviewPath(runtime.editor.workspace.itineraryId)} target="_blank" rel="noopener noreferrer">
            <Eye size={15} aria-hidden="true" />
            <span>行程预览</span>
          </a>
          <a className="fulfillment-order-related-links__link" href={buildItineraryBudgetPath(runtime.editor.workspace.itineraryId)} target="_blank" rel="noopener noreferrer">
            <Calculator size={15} aria-hidden="true" />
            <span>行程核价</span>
          </a>
          {runtime.editor.workspace.sourceBudgetOrderId ? (
            <a className="fulfillment-order-related-links__link" href={`/quote/budgets/${encodeURIComponent(runtime.editor.workspace.sourceBudgetOrderId)}/customer-quote`} target="_blank" rel="noopener noreferrer">
              <ReceiptText size={15} aria-hidden="true" />
              <span>行程报价</span>
            </a>
          ) : null}
          {runtime.editor.workspace.projectId ? (
            <a className="fulfillment-order-related-links__link" href={`/crm/order-handover/${encodeURIComponent(runtime.editor.workspace.projectId)}`} target="_blank" rel="noopener noreferrer">
              <UsersRound size={15} aria-hidden="true" />
              <span>人员名单</span>
            </a>
          ) : null}
        </div>
      </nav></div>
        <div className="confirmation-workspace-actions">
          <div className="confirmation-command-buttons">
            {canFinalize && beforeFinalization ? (
              <ConfirmationButton
                variant="outline"
                loading={runtime.finalizing}
                disabled={feeAdjustmentEditing || runtime.writePending || hasUnsavedWork || Boolean(runtime.errorCode)}
                onClick={async () => {
                  const generation = ++actionRequestGenerationRef.current;
                  try {
                    setActionFeedback(null);
                    const saved = await runtime.finalize();
                    const snapshot = actionFeedbackSnapshot(saved, saved.lines);
                    if (actionRequestGenerationRef.current !== generation || unappliedWorkRef.current
                      || (activeActionSnapshotRef.current !== currentActionSnapshot && activeActionSnapshotRef.current !== snapshot)) return;
                    setActionFeedback({ snapshot, tone: "success", title: "确认件已定稿", description: "当前确认费用已形成正式版本。", issues: [] });
                  } catch (error) {
                    if (actionRequestGenerationRef.current !== generation || unappliedWorkRef.current || activeActionSnapshotRef.current !== currentActionSnapshot) return;
                    const issues = error instanceof FulfillmentOrderUserFacingError && error.message.startsWith("定稿前校验未通过：")
                      ? runtime.lines.flatMap((line) => validateFulfillmentLinesBeforeFinalize([line]).map((message) => ({ message, lineId: line.id }))) : [];
                    setActionFeedback({ snapshot: currentActionSnapshot, tone: issues.length ? "warning" : "error", title: issues.length ? "定稿前还需补齐以下内容" : "暂时无法定稿", description: issues.length ? "已保存的草稿仍然保留。点击对应明细补齐后，再次定稿。" : getSafeFulfillmentOrderActionErrorMessage(error, "确认件定稿失败，请稍后重试"), issues });
                  }
                }}
              >
                定稿
              </ConfirmationButton>
            ) : null}
            {canFinalize && !beforeFinalization ? (
              <ConfirmationButton
                variant="outline"
                loading={runtime.unfinalizing}
                disabled={feeAdjustmentEditing || runtime.writePending}
                onClick={async () => {
                  try {
                    await runtime.unfinalize();
                    Message.success("确认件已取消定稿");
                  } catch (error) {
                    Message.error(getSafeFulfillmentOrderActionErrorMessage(
                      error,
                      "取消定稿失败，请稍后重试",
                    ));
                  }
                }}
              >
                取消定稿
              </ConfirmationButton>
            ) : null}
            {canUpdate ? (
              <>
                <ConfirmationButton
                  variant="outline"
                  title={runtime.orders.length > 0
                    ? "已建立结算主体费用，不能用历史数据覆盖；团中费用变化请通过费用调整处理。"
                    : undefined}
                  disabled={feeAdjustmentEditing || runtime.writePending || runtime.orders.length > 0 || formulaDirty}
                  aria-describedby={runtime.orders.length > 0 ? "fulfillment-history-disabled-reason" : undefined}
                  onClick={() => setHistoryVisible(true)}
                >
                  获取历史数据
                </ConfirmationButton>
                {runtime.orders.length > 0 ? <span className="sr-only" id="fulfillment-history-disabled-reason">已建立结算主体费用，不能用历史数据覆盖；团中费用变化请通过费用调整处理。</span> : null}
              </>
            ) : null}
            {canUpdate ? (
              <ConfirmationButton
                variant="default"
                loading={runtime.saving}
                disabled={feeAdjustmentEditing || runtime.writePending || !runtime.isDirty || formulaDirty}
                onClick={async () => {
                  const generation = ++actionRequestGenerationRef.current;
                  try {
                    setActionFeedback(null);
                    const saved = await runtime.save();
                    const snapshot = actionFeedbackSnapshot(saved, saved.lines);
                    if (actionRequestGenerationRef.current !== generation || unappliedWorkRef.current
                      || (activeActionSnapshotRef.current !== currentActionSnapshot && activeActionSnapshotRef.current !== snapshot)) return;
                    const issues = saved.lines.flatMap((line) => validateFulfillmentLinesBeforeFinalize([line]).map((message) => ({ message, lineId: line.id })));
                    setActionFeedback({ snapshot, tone: "success", title: "草稿已保存", description: issues.length ? `录入数据已保存，另有 ${issues.length} 处内容需在定稿前补齐。` : "录入数据已保存，可继续编辑或核对后定稿。", issues });
                  } catch (error) {
                    if (actionRequestGenerationRef.current !== generation || unappliedWorkRef.current || activeActionSnapshotRef.current !== currentActionSnapshot) return;
                    const issues = error instanceof FulfillmentOrderUserFacingError && error.message.startsWith("保存前校验未通过：")
                      ? runtime.lines.flatMap((line) => validateFulfillmentLinesBeforeSave([line]).map((message) => ({ message, lineId: line.id }))) : [];
                    setActionFeedback({ snapshot: currentActionSnapshot, tone: "error", title: "草稿暂未保存", description: issues.length ? "以下内容无法正确写入，请修正后重试；当前输入仍保留。" : getSafeFulfillmentOrderActionErrorMessage(error, "保存确认行池失败，请稍后重试"), issues });
                  }
                }}
              >
                保存草稿
              </ConfirmationButton>
            ) : null}
            {canAccessFeeAdjustment ? (
              <ConfirmationButton
                variant="outline"
                disabled={runtime.writePending || feeAdjustmentPending || feeAdjustmentEditing}
                title={feeAdjustmentEditing ? "请先完成或取消当前费用调整" : undefined}
                onClick={() => setFeeAdjustmentDrawerVisible(true)}
              >
                费用调整
              </ConfirmationButton>
            ) : null}
          </div>
        </div>
      </header>

      <div className="confirmation-layout"><ConfirmationSidebar workspace={runtime.editor.workspace} lines={runtime.lines} currentAmount={runtime.localCurrentAmount}>
        <ConfirmationSettlementGroups
          key={workspaceId}
          groups={settlementGroups.groups}
          ungroupedLines={settlementGroups.ungroupedLines}
          adjustmentChanges={runtime.workspaceChanges}
          currentVersionId={runtime.editor.workspace.currentVersionId}
          adjustmentLoading={runtime.workspaceChangesLoading}
          adjustmentError={runtime.workspaceChangesError}
          onRetryAdjustments={() => { void runtime.refetchWorkspaceChanges(); }}
          busy={runtime.writePending || feeAdjustmentPending}
          readOnly={!canUpdate || feeAdjustmentEditing}
          unapplied={hasUnappliedWork}
          onGenerate={generateForSubject}
          onSelectOrder={runtime.selectOrder}
          agreement={{
            selectedOrderId: runtime.selectedOrderId,
            detail: runtime.orderDetail,
            loading: runtime.orderDetailLoading,
            error: runtime.orderDetailError,
            onRetry: () => void runtime.refetchOrderDetail(),
            canExport,
            canArchive,
            exporting: lifecycle.exporting,
            uploading: lifecycle.uploading,
            actionPending: lifecycle.actionPending || runtime.writePending,
            hasUnsavedChanges: hasUnsavedWork,
            onDownload: lifecycle.download,
            onDownloadArchive: lifecycle.downloadArchive,
            onUpload: lifecycle.upload,
          }}
          feedback={renderGenerateValidation}
        />
      </ConfirmationSidebar><div className="confirmation-main">
      {runtime.loadError ? (
        <div className="fulfillment-order-inline-warning" role="alert">
          工作区刷新失败，当前编辑内容已保留，请重试。
          <ConfirmationButton variant="outline" onClick={() => void runtime.refetch()}>重试刷新</ConfirmationButton>
        </div>
      ) : null}

      {runtime.errorCode === "FULFILLMENT_ORDER_REVISION_CONFLICT" ? (
        <div className="fulfillment-order-inline-warning" role="alert">
          该确认件已被其他人更新，当前本地草稿已保留。重新加载将放弃当前未保存内容。
          <Button loading={runtime.reloading} disabled={runtime.writePending}
            onClick={async () => {
              if (!window.confirm("重新加载将放弃当前未保存明细、待加入文本及编辑输入，确认继续吗？")) return;
              try {
                if (!await runtime.reloadLatest()) return;
                setEditingLine(null);
                setEntryGeneration(value => value + 1);
                setEntryDirty({}); setEntryCategory(null); setRailwayEntryOpen(false);
                setTicketInitialVisitId(null); setTicketDirtyVisitIds([]);
                setGenerateValidation(null);
                setQuickAdjustmentLine(null);
                setNewLineNo(null);
                Message.success("已加载最新确认件数据");
              } catch (error) {
                Message.error(getSafeFulfillmentOrderActionErrorMessage(error, "重新加载失败，当前草稿已保留，请重试"));
              }
            }}
          >重新加载最新数据</Button>
        </div>
      ) : null}
      {runtime.errorCode === "FULFILLMENT_ORDER_INVALID_LINE_INPUT" ? (
        <div className="fulfillment-order-inline-warning" role="alert">
          确认行数值不合法，请检查数量、计费次数和单价。
        </div>
      ) : null}
      <section className="fulfillment-order-main-workspace" aria-label="确认件主工作区">
        {feeAdjustmentRequestId ? (
          <div className="fulfillment-order-fee-adjustment-mode" role="status">
            <Typography.Text type="warning">
              正在编辑费用调整：{feeAdjustmentRequestText ?? "正在加载费用调整需求…"}
            </Typography.Text>
          </div>
        ) : null}
        <FeeAdjustmentWorkspacePanel
        formatAmount={formatConfirmationAmount}
          key={workspaceId}
          workspaceId={workspaceId}
          sourceLines={runtime.lines}
          canAccess={canAccessFeeAdjustment}
          canUpdate={canUpdateFeeAdjustment}
          canFinalize={canFinalizeFeeAdjustment}
          activeRequestId={feeAdjustmentRequestId}
          onActiveRequestChange={setFeeAdjustmentRequestId}
          onActiveRequestTextChange={setFeeAdjustmentRequestText}
          adjustingLine={feeAdjustmentLine}
          onAdjustingLineChange={setFeeAdjustmentLine}
          onDirtyChange={setFeeAdjustmentDirty}
          onPendingChange={setFeeAdjustmentPending}
          embedded
          groupDescriptionAmounts
          sourceLinesSlot={(
            <>
              {newLineNo !== null ? <ConfirmationButton className="confirmation-reveal-new" disabled={runtime.writePending} onClick={() => {
                runtime.setFilters({ resourceType: "all", assignment: "all", sourceDifferenceOnly: false });
                setRevealLineId(runtime.lines.find((line) => line.lineNo === newLineNo)?.id ?? null);
                setNewLineNo(null);
              }}>查看新加入明细</ConfirmationButton> : null}

              <section className="fulfillment-order-workbench">
                <div className="fulfillment-order-lines-card">
                  <ConfirmationEditorLinePool
                    workspaceId={workspaceId}
                    budgetImportReport={runtime.editor.workspace.budgetImport}
                    reviewActions={pendingHistoryLines.length > 0 && pendingHistoryDateCount === 0
                      && canUpdate && !hasUnsavedWork && !feeAdjustmentEditing
                      && (!runtime.writePending || runtime.confirmingHistoryLines) ? (
                      <Button
                        type="text"
                        size="small"
                        loading={runtime.confirmingHistoryLines}
                        disabled={runtime.confirmingHistoryLines}
                        title="确认已逐项核对日期、数量、计费次数和金额"
                        onClick={async () => {
                          try {
                            await runtime.confirmHistoryLines();
                            Message.success("历史明细已全部确认");
                          } catch (error) {
                            Message.error(getSafeFulfillmentOrderActionErrorMessage(
                              error, "确认历史明细失败，请稍后重试",
                            ));
                          }
                        }}
                      >标记全部已核对</Button>
                    ) : null}
                    actionFeedback={!hasUnappliedWork && actionFeedback?.snapshot === currentActionSnapshot ? actionFeedback : null}
                    onLocateIssue={(lineId) => { setRevealLineId(null); requestAnimationFrame(() => setRevealLineId(lineId)); }}
                    onDeleteLine={feeAdjustmentEditing ? undefined : (line) => { void applyBatch({ updates: [], additions: [], removals: [line.id] }).catch(error => Message.error(generateErrorMessage(error))); }}
                    onOpenEntry={openCategoryEntry}
                    ticketVisits={ticketVisits}
                    ticketDirtyVisitIds={ticketDirtyVisitIds}
                    onOpenTicketPoint={id => { setTicketInitialVisitId(id); setEntryExistingLineId(null); setEntryNewOnly(false); setEntryCategory("ticket"); setRailwayEntryOpen(false); }}
                    baselineSlot={runtime.isDirty && (adjustableLineIds.size > 0 || !beforeFinalization) ? (
                      <p className="confirmation-command-notices" role="status">请先保存确认行池，再进行增减。</p>
                    ) : null}
                    readOnly={!canUpdate}
                    entryDisabled={!canUpdate || feeAdjustmentEditing || formulaDirty}
                    entryEditDisabledReason={entryEditDisabledReason}
                    editDisabled={formulaDirty}
                    inlineSavePending={runtime.writePending || feeAdjustmentPending}
                    onFormulaDirtyChange={setFormulaDirty}
                    formulaResetGeneration={entryGeneration}
                    onUpdateLine={(id, patch) => { runtime.applyDraftBatch({ updates: [{ id, patch }], additions: [], removals: [] }); }}
                    onDeleteSelected={feeAdjustmentEditing ? undefined : () => { void applyBatch({updates: [], additions: [], removals: [...runtime.selectedIds]}).catch(error => Message.error(generateErrorMessage(error))); }}
                    lines={runtime.lines}
                    visibleLines={runtime.visibleLines}
                    filters={runtime.filters}
                    onFiltersChange={runtime.setFilters}
                    selectedIds={runtime.selectedIds}
                    selectableIds={runtime.selectableLineIds}
                    readonlyLineIds={readonlyLineIds}
                    adjustableLineIds={adjustableLineIds}
                    revealLineId={revealLineId}
                    interactionDisabled={runtime.writePending || feeAdjustmentPending}
                    adjustmentDisabled={runtime.isDirty}
                    onSelectedIdsChange={runtime.setSelectedIds}
                    onEdit={openLineEditor}
                    onQuickAdjust={setQuickAdjustmentLine}
                    onFeeAdjust={feeAdjustmentRequestId && canUpdateFeeAdjustment && canFinalizeFeeAdjustment ? (line) => { if (!feeAdjustmentPending) setFeeAdjustmentLine(line); } : undefined}
                    expandedAdjustmentLineId={changeHistoryLine?.id ?? null}
                    adjustmentDetail={null}
                    currentVersionId={runtime.editor.workspace.currentVersionId}
                    adjustmentLoading={
                      runtime.workspaceChangesLoading
                    }
                    adjustmentError={runtime.workspaceChangesError}
                    adjustmentChanges={runtime.workspaceChanges}
                    onToggleAdjustments={(line) => {
                      const closing = changeHistoryLine?.id === line.id;
                      setChangeHistoryLine(closing ? null : line);
                    }}
                    onRetryAdjustments={() => {
                      void runtime.refetchWorkspaceChanges();
                    }}
                    settlementBar={renderGenerateValidation()}
                    selectionActions={(
                      <>
                        <ConfirmationSubjects selectedLines={selectedLines} runtime={runtime} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending || feeAdjustmentPending || formulaDirty} onApply={applyBatch} />

                      </>
                    )}
                  />
                </div>
              </section>
            </>
          )}
          renderQuickEntry={(onCreated, controls) => (
            <FulfillmentOrderFeeAdjustmentQuickEntry
              disabled={!canUpdateFeeAdjustment || controls.disabled}
              onPendingTextChange={controls.onDirtyChange}
              workspaceStatus={currentEditor.workspace.status}
              itineraryDays={(currentEditor.workspace.routeDays ?? []).flatMap((day) =>
                day.date ? [{ dayNo: day.dayNo, date: day.date }] : [],
              )}
              serviceProviderOptions={rawQuickEntryCandidates}
              settlementSupplierOptions={runtime.settlementSupplierOptions ?? runtime.supplierOptions}
              serviceProviderResourceType={runtime.quickEntryResourceType}
              serviceProviderSearchKeyword={runtime.quickEntrySupplierSearch}
              serviceProviderLoading={quickEntrySupplierLoading}
              settlementSupplierLoading={settlementSupplierLoading}
              serviceProviderError={quickEntrySupplierError}
              settlementSupplierError={settlementSupplierError}
              serviceProviderErrorCode={quickEntrySupplierErrorCode}
              settlementSupplierErrorCode={settlementSupplierErrorCode}
              onRetryServiceProviders={retryQuickEntrySuppliers}
              onRetrySettlementSuppliers={retrySettlementSuppliers}
              onServiceProviderResourceTypeChange={runtime.setQuickEntryResourceType}
              onSearchServiceProviders={runtime.searchQuickEntryFulfillmentSuppliers ?? runtime.searchFulfillmentSuppliers}
              onSearchSettlementSuppliers={runtime.searchSettlementSuppliers ?? runtime.searchFulfillmentSuppliers}
              searchResourceCandidates={runtime.searchResourceCandidates}
              searchTransportPoints={runtime.searchTransportPoints}
              searchTransportResourceCandidates={runtime.searchTransportResourceCandidates}
              onCreated={onCreated}
            />
          )}
        />
        <ConfirmationSheet
          title="费用调整待办"
          className="confirmation-adjustment-pending-sheet"
          description="销售发起的费用调整申请，选择一笔后在当前确认件中处理。"
          open={feeAdjustmentDrawerVisible}
          onClose={() => setFeeAdjustmentDrawerVisible(false)}
        >
          <FeeAdjustmentWorkspacePanel
        formatAmount={formatConfirmationAmount}
            workspaceId={workspaceId}
            sourceLines={runtime.lines}
            canAccess={canAccessFeeAdjustment}
            canUpdate={canUpdateFeeAdjustment}
            canFinalize={canFinalizeFeeAdjustment}
            onActiveRequestTextChange={setFeeAdjustmentRequestText}
            adjustingLine={feeAdjustmentLine}
            onAdjustingLineChange={setFeeAdjustmentLine}
            activeRequestId={null}
            onActiveRequestChange={(requestId) => {
              if (runtime.writePending || feeAdjustmentPending || feeAdjustmentEditing) return;
              setFeeAdjustmentRequestId(requestId);
              setFeeAdjustmentDrawerVisible(false);
            }}
            embedded
          />
        </ConfirmationSheet>
      </section>

      <FulfillmentOrderFeeHistoryPanel
        collapsible
        key={workspaceId}
        workspaceId={workspaceId}
        canAccessRequests={canAccessFeeAdjustment}
        changes={runtime.workspaceChanges ?? []}
        changesLoading={runtime.workspaceChangesLoading}
        changesError={runtime.workspaceChangesError}
        onRetryChanges={() => void runtime.refetchWorkspaceChanges()}
      />

      </div></div>
      <ConfirmationStayEntryDrawer key={`stay-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "stay" ? entryExistingLineId : null} open={entryCategory === "stay"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.stay === dirty ? previous : ({ ...previous, stay: dirty }))} onApply={applyBatch} />
      <ConfirmationAirEntryDrawer key={`air-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "air" ? entryExistingLineId : null} open={entryCategory === "air"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.air === dirty ? previous : ({ ...previous, air: dirty }))} onApply={applyBatch} />
      <ConfirmationShipEntryDrawer key={`ship-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "ship" ? entryExistingLineId : null} open={entryCategory === "ship"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.ship === dirty ? previous : ({ ...previous, ship: dirty }))} onApply={applyBatch} />
      <ConfirmationBusEntryDrawer key={`bus-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "bus" ? entryExistingLineId : null} open={entryCategory === "bus"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.bus === dirty ? previous : ({ ...previous, bus: dirty }))} onApply={applyBatch} />
      <ConfirmationTicketEntryDrawer key={`ticket-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "ticket" ? entryExistingLineId : null} initialVisitId={ticketInitialVisitId} onDraftVisitsChange={setTicketDirtyVisitIds} open={entryCategory === "ticket"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.ticket === dirty ? previous : ({ ...previous, ticket: dirty }))} onApply={applyBatch} />
      <ConfirmationMealEntryDrawer key={`meal-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "meal" ? entryExistingLineId : null} open={entryCategory === "meal"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.meal === dirty ? previous : ({ ...previous, meal: dirty }))} onApply={applyBatch} />
      <ConfirmationCrewEntryDrawer key={`crew-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "crew" ? entryExistingLineId : null} open={entryCategory === "crew"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.crew === dirty ? previous : ({ ...previous, crew: dirty }))} onApply={applyBatch} />
      <ConfirmationMiscEntryDrawer key={`misc-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryCategory === "misc" ? entryExistingLineId : null} open={entryCategory === "misc"} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => setEntryCategory(null)} onDirtyChange={dirty => setEntryDirty(previous => previous.misc === dirty ? previous : ({ ...previous, misc: dirty }))} onApply={applyBatch} />
      <ConfirmationRailwayDrawer key={`rail-${workspaceId}-${entryGeneration}`} newOnly={entryNewOnly} existingLineId={entryExistingLineId} category="train" open={railwayEntryOpen} runtime={runtime} editableIds={ordinaryEditableIds} disabled={feeAdjustmentEditing || !canUpdate || runtime.writePending} onClose={() => { setRailwayEntryOpen(false); setEntryCategory(null); }} onDirtyChange={dirty => setEntryDirty(previous => previous.train === dirty ? previous : ({ ...previous, train: dirty }))} onApply={applyBatch} />
      <FulfillmentOrderLineDetailDrawer
        line={editingLine}
        onClose={() => setEditingLine(null)}
      />
      <FulfillmentOrderHistoryDrawer
        visible={historyVisible}
        workspaceId={runtime.editor.workspace.id}
        itineraryId={runtime.editor.workspace.itineraryId}
        revision={Number(runtime.editor.workspace.revision)}
        onClose={() => setHistoryVisible(false)}
        onReplaced={(courseRouteEditor) => {
          runtime.setAuthoritativeEditor(toLegacyFulfillmentEditor(courseRouteEditor));
          setEditingLine(null);
          setGenerateValidation(null);
        }}
      />
      <FulfillmentOrderQuickAdjustmentModal
        formatAmount={formatConfirmationAmount}
        line={quickAdjustmentLine}
        onDirtyChange={setAdjustmentDirty}
        submitting={runtime.changingPostSigningLine}
        onClose={() => setQuickAdjustmentLine(null)}
        onSubmit={async (line, adjustment, reason) => {
          await runtime.applyLineAdjustment(line.id, adjustment, reason);
          Message.success("增减项已记录");
        }}
      />
    </main>
  );
}
